/**
 * Pull the most actionable human-readable message out of an API error.
 *
 * Galaxy-server wraps downstream errors as
 *   `{ message, statusCode, error: <downstream response body> }`
 * and `handleError` in services/api.ts puts that body on `error.data`.
 *
 * Order of preference:
 *   1. `data.message`                — galaxy-server's extracted reason
 *   2. `data.errors` (rails plural)  — direct rails-api responses
 *   3. `data.error.errors` (nested)  — galaxy-server wrap of rails plural
 *   4. `data.error`                  — single-string fallback
 *   5. `error.message`               — axios/network message
 */
const joinIfArray = (value: unknown): string | undefined => {
  if (Array.isArray(value)) {
    const joined = value.filter(Boolean).join('; ');
    return joined || undefined;
  }
  if (typeof value === 'string' && value.trim()) return value;
  return undefined;
};

export const extractApiErrorMessage = (error: unknown, fallback = 'Unknown error'): string => {
  if (!error) return fallback;

  const anyErr = error as any;
  const data = anyErr?.data ?? anyErr?.response?.data;

  if (typeof data?.message === 'string' && data.message.trim()) {
    return data.message;
  }

  const rootErrors = joinIfArray(data?.errors);
  if (rootErrors) return rootErrors;

  const nestedErrors = joinIfArray(data?.error?.errors);
  if (nestedErrors) return nestedErrors;

  if (typeof data?.error === 'string' && data.error.trim()) {
    return data.error;
  }

  if (typeof anyErr?.message === 'string' && anyErr.message.trim()) {
    return anyErr.message;
  }

  return fallback;
};
